import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Mail, FileText, Link2, PoundSterling, Plane, FileSearch, ChevronRight } from 'lucide-react';
import { SwiftHeroGeometric } from '../components/ui/swift-hero-geometric';
import AnimatedGradientBackground from '../components/ui/animated-gradient-background';

// Scrolling ticker component
const ScrollingTicker = () => {
  const messages = [
    "YOUR CONVEYANCER WILL GET BACK TO YOU SHORTLY",
    "NO NEWS IS GOOD NEWS",
    "THE SEARCHES HAVE BEEN ORDERED",
    "WE ARE WAITING ON THE OTHER SIDE",
    "YOUR FILE IS WITH THE FEE EARNER",
    "THE CHAIN IS PROGRESSING WELL",
    "WE WILL CHASE THIS UP FOR YOU",
    "EXCHANGE IS IMMINENT"
  ];

  return (
    <div className="overflow-hidden bg-white/5 py-4 border-y border-white/10">
      <div className="animate-scroll-left whitespace-nowrap">
        {[...messages, ...messages].map((msg, i) => (
          <span key={i} className="inline-block mx-12 text-white/40 text-sm font-mono">
            {msg} <span className="text-[#57D961]">•</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// Counter animation component
const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const SwiftAiPage = () => {
  const features = [
    {
      icon: Mail,
      title: "Instant Reply Mode",
      tagline: "SWIFT.ai replies faster than your conveyancer reads the subject line.",
      description: "Replies to every email within 60 seconds. No more 'I'll get back to you' followed by three weeks of silence."
    },
    {
      icon: FileText,
      title: "Jargon Translator",
      tagline: "Finally, a conveyancer that speaks your language.",
      description: "Translates every piece of legal correspondence into plain English. Turns a 14-page lease report into a one-paragraph summary."
    },
    {
      icon: FileSearch,
      title: "Never Loses a Document",
      tagline: "You sent it once. SWIFT.ai remembers. Revolutionary, we know.",
      description: "Zero document requests. Never asks you to resend something you already sent. Never loses a form."
    },
    {
      icon: Link2,
      title: "Chain Whisperer",
      tagline: "SWIFT.ai doesn't just manage your chain. It manages everyone else's conveyancer too.",
      description: "Monitors every link in the chain simultaneously. If a conveyancer hasn't opened their file in 8 days, SWIFT.ai sends them a nudge."
    },
    {
      icon: PoundSterling,
      title: "No Surprise Fees",
      tagline: "The price you see is the price you pay. We know. We're as shocked as you are.",
      description: "Your quote is your quote. No hidden disbursements. No mysterious 'administration charges' that appear after you've committed."
    },
    {
      icon: Plane,
      title: "Holiday-Proof",
      tagline: "SWIFT.ai doesn't do out-of-office. Because your house move doesn't either.",
      description: "Never goes on annual leave mid-chain. Doesn't hand your file to a colleague who has never read it."
    },
    {
      icon: FileSearch,
      title: "Leasehold Decoder",
      tagline: "SWIFT.ai reads the small print so you don't have to. Then tells you what it means. In English.",
      description: "Scans every lease in 0.3 seconds. Flags escalating ground rents, punitive service charges, and restrictive covenants."
    }
  ];

  const testimonials = [
    {
      quote: "My conveyancer replied to my email! It only took 11 days, 3 chasers, and a strongly worded voicemail. The reply said 'noted.' That was it. Just 'noted.' I am now on first-name terms with my GP's receptionist because of the stress.",
      author: "Verified Home Mover",
      detail: "Purchase took 187 days"
    },
    {
      quote: "I was asked to provide proof of identity four times. The fourth time, my conveyancer asked me to 'resubmit as the file has been misplaced.' I have now been photographed holding my passport more times than an international spy.",
      author: "Verified Home Mover",
      detail: "Nearly pulled out twice"
    },
    {
      quote: "I called the buyer's solicitor 14 times in three weeks. On the 14th call, I was told 'the fee earner is on leave until the 28th.' The 28th of which month was not specified. The sale fell through on Day 97. I lost £8,400 in commission.",
      author: "Verified Estate Agent",
      detail: "Considering a career change"
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Floating Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/50 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src="https://customer-assets.emergentagent.com/job_942b5a57-ac76-485d-bb84-34f38f7c7749/artifacts/86h50e59_Swift%20Ai.png" 
            alt="Gazeal SWIFT AI" 
            className="h-10"
          />
          <a 
            href="https://advance.gazeal.co.uk" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white/60 hover:text-[#57D961] text-sm transition-colors hidden md:block"
          >
            Visit Gazeal Advance →
          </a>
        </div>
      </header>

      {/* Hero Section with Animated Background */}
      <SwiftHeroGeometric
        badge="Introducing SWIFT.ai"
        title1="Ah, the UK conveyancing market."
        title2="Where dreams go to age like a fine, stressful wine."
        subtitle="Because 135 days to exchange contracts is not a process. It's a hostage situation."
      >
        {/* Stats Bar - White Glossy 3D Effect */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-8 max-w-4xl mx-auto px-4">
          {[
            { value: 135, suffix: '', label: 'DAYS TO EXCHANGE' },
            { value: 45, suffix: '%', label: 'SLOWER THAN 2019' },
            { value: 221, suffix: '', label: 'DAYS TO MOVE HOME' },
            { value: 6000, suffix: '', label: 'SALES COLLAPSE WEEKLY' },
            { value: 3337, suffix: '', label: 'AVG COST OF FALL-THROUGH', prefix: '£' }
          ].map((stat, i) => (
            <div key={i} className="bg-gradient-to-b from-white to-gray-200 rounded-xl p-3 shadow-[0_4px_20px_rgba(255,255,255,0.15),inset_0_1px_0_rgba(255,255,255,0.8),inset_0_-2px_4px_rgba(0,0,0,0.1)]">
              <p className="text-2xl md:text-3xl font-bold text-gray-900">
                {stat.prefix}<AnimatedCounter end={stat.value} suffix={stat.suffix} />
              </p>
              <p className="text-gray-600 text-[10px] mt-1 uppercase tracking-wider">{stat.label}</p>
            </div>
          ))}
        </div>
      </SwiftHeroGeometric>

      {/* UK's First AI Conveyancer Section - Combined */}
      <section className="py-12 px-6 relative overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left - Text Content */}
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                Introducing the UK's First{' '}
                <span className="text-[#57D961]">AI Conveyancer</span>
              </h2>
              
              <p className="text-xl text-white/80 mb-6">
                Meet SWIFT.ai, the AI conveyancer that does everything you wish your current one could do.
              </p>
              
              <p className="text-white/60 mb-8 leading-relaxed">
                Replies instantly. Never loses documents. Doesn't go on holiday mid-chain. Explains legal jargon in plain English. Sounds too good to be true? Keep reading.
              </p>

              {/* SWIFT Breakdown - Powerful & Satirical */}
              <div className="space-y-3">
                <p className="text-white/40 text-sm uppercase tracking-[0.2em] mb-4">What does SWIFT stand for?</p>
                {[
                  { letter: 'S', word: 'old', desc: 'Your property, finally.' },
                  { letter: 'W', word: 'ithout', desc: 'The endless waiting game.' },
                  { letter: 'I', word: 'nfinite', desc: 'Because 135 days feels like forever.' },
                  { letter: 'F', word: 'rustrating', desc: 'No more "we\'ll get back to you."' },
                  { letter: 'T', word: 'imescales', desc: 'Weeks, not months.' }
                ].map((item, i) => (
                  <div 
                    key={i} 
                    className="flex items-center gap-4 py-2 border-b border-white/10 last:border-0 animate-slide-in"
                    style={{ animationDelay: `${i * 0.1}s` }}
                  >
                    <span className="text-lg font-medium">
                      <span className="text-[#57D961]">{item.letter}</span>
                      <span className="text-white">{item.word}</span>
                    </span>
                    <span className="text-white/40 text-sm ml-auto hidden sm:block">{item.desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative flex items-center justify-center min-h-[400px]">
              {/* Scales Image */}
              <img 
                src="https://customer-assets.emergentagent.com/job_942b5a57-ac76-485d-bb84-34f38f7c7749/artifacts/gwv5tigh_Swift%20Icon.png" 
                alt="SWIFT AI Legal Scales" 
                className="w-full max-w-md"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-14 px-6 bg-gradient-to-b from-transparent via-white/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              AI Conveyancing is <span className="text-[#57D961]">here!</span>
            </h2>
            <p className="text-white/60 text-lg">
              Every feature is something that should already exist. But doesn't.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i} 
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#57D961]/50 transition-all group"
              >
                <feature.icon className="w-10 h-10 text-[#57D961] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-[#57D961]/80 text-sm italic mb-3">"{feature.tagline}"</p>
                <p className="text-white/50 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-14 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Old Way vs. <span className="text-[#57D961]">The SWIFT Way</span>
            </h2>
          </div>

          <div className="mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_942b5a57-ac76-485d-bb84-34f38f7c7749/artifacts/pic69efo_image_2026-04-01_084642643.png" 
              alt="Timeline comparison" 
              className="w-full max-w-3xl mx-auto rounded-xl"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Old Way */}
            <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-red-400 mb-6">The Old Way</h3>
              <div className="space-y-4">
                {[
                  { day: 1, text: "Offer accepted. Excitement peaks." },
                  { day: 14, text: "Conveyancer acknowledges instruction. First silence begins." },
                  { day: 30, text: "You chase. 'Searches have been ordered.' You don't know what this means." },
                  { day: 60, text: "You chase again. 'Waiting on the other side.' They're also waiting." },
                  { day: 90, text: "Your mortgage offer is about to expire. Panic." },
                  { day: 135, text: "Exchange. Maybe. If nobody pulls out first." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="text-red-400/60 text-sm w-16 shrink-0">Day {item.day}</span>
                    <span className="text-white/70 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* SWIFT Way */}
            <div className="bg-[#57D961]/5 border border-[#57D961]/20 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-[#57D961] mb-6">The SWIFT Way</h3>
              <div className="space-y-4">
                {[
                  { day: 1, text: "Offer accepted. Gazeal Reservation Agreement signed. Sale secured." },
                  { day: 1, text: "Agent gets paid via Gazeal Advance. No waiting for completion." },
                  { day: 2, text: "SWIFT.ai instructs. All documents requested and verified automatically." },
                  { day: 14, text: "All enquiries raised and responded to. Lease decoded. Risk report delivered." },
                  { day: 28, text: "Exchange. Four weeks. Not four months." },
                  { day: 35, text: "Completion. You're in your new home." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <span className="text-[#57D961]/60 text-sm w-16 shrink-0">Day {item.day}</span>
                    <span className="text-white/70 text-sm">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Triangle of Frustration - Testimonials */}
      <section className="py-14 px-6 bg-gradient-to-b from-transparent via-white/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              The Triangle of <span className="text-red-400">Frustration</span>
            </h2>
            <p className="text-white/60">Real experiences. Real pain. Real need for change.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <p className="text-white/80 text-sm italic mb-6 leading-relaxed">"{t.quote}"</p>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-white font-medium text-sm">{t.author}</p>
                  <p className="text-white/40 text-xs">{t.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Reveal / Footer */}
      <section className="relative py-14 px-6 border-t border-white/10 overflow-hidden">
        {/* Animated Gradient Background */}
        <AnimatedGradientBackground 
          Breathing={true}
          startingGap={100}
          breathingRange={10}
          animationSpeed={0.03}
          gradientColors={[
            "#000000",
            "#000000", 
            "#57D961",
            "#3d9942",
            "#1f5c24",
            "#0f3312",
            "#000000"
          ]}
          gradientStops={[0, 30, 50, 65, 80, 92, 100]}
          topOffset={50}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Letter-style Note */}
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
              Of course, today is April 1st... <span className="text-black">But what if?</span>
            </h2>
            
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p>
                Look, SWIFT.ai isn't real. Not yet. But let me tell you what is real. 135 days to exchange contracts. That's real. 6,000 sales collapsing every single week. That's real. Families losing an average of £3,337 when their move falls apart. That's painfully, brutally real.
              </p>
              
              <p>
                We built this page to make you laugh. But honestly? There's nothing funny about a system that's 45% slower than it was in 2019 and getting worse, not better. Buyers are being ghosted. Sellers are watching chains collapse in slow motion. Agents are losing thousands in commission on deals that should have crossed the line months ago. And conveyancers? They're buried under impossible caseloads in a system that hasn't given them the tools to keep up. Nobody is winning here.
              </p>
              
              <p>
                So yes, the AI Conveyancer is an April Fools joke. But here's the thing. <strong>Two out of the three things on this page are not a joke.</strong> They exist. Right now. Today.
              </p>
              
              <p>
                <strong>Gazeal's Reservation Agreement</strong> means both parties put their money where their mouth is at the point of offer. Not a handshake. Not a promise. A financial commitment. 94% of Gazeal sales complete. The national average? 70%. That's not a statistic you frame on your wall. That's 6,000 families a week who don't lose their home, their deposit, and their sanity.
              </p>
              
              <p>
                <strong>Gazeal Advance</strong> means your agent gets paid the moment the sale is agreed. Not at completion. Not after months of chasing a chain that might never finish. At sale agreed. Because if the sale is secured, why should anyone wait six months to get paid for the work they've already done?
              </p>
              
              <p>
                Two out of three. The security is real. The early payment is real. The only thing missing is the AI conveyancer. And honestly? Give it time.
              </p>
              
              <p>
                We're not here to replace conveyancers. They do essential, difficult work and the good ones are worth their weight in gold. But the system around them is broken. It needs speed. It needs technology. It needs commitment from every side of the transaction. Until that happens, at least we can make sure that when a sale is agreed, it stays agreed. And that the people who make it happen get paid for it without waiting half a year.
              </p>
              
              <p className="font-medium text-gray-900">
                That's what Gazeal does. And that's not an April Fools joke.
              </p>
              
              <div className="pt-6 border-t border-gray-200 mt-8">
                <p className="font-semibold text-gray-900">Bryan Mansell</p>
                <p className="text-gray-500">CEO, Gazeal</p>
              </div>
            </div>
          </div>

          {/* What Gazeal Can Do Today */}
          <div className="bg-white rounded-2xl p-8 mb-8 shadow-xl">
            <h3 className="text-xl font-semibold mb-6 text-gray-900 text-center">What Gazeal can do today:</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-gray-900 font-semibold mb-2">Gazeal Reservation Agreement</h4>
                <p className="text-gray-600 text-sm">Both parties commit financially at the point of offer. 94% of Gazeal sales complete, vs 70% without. That's 6,000 families a week who don't lose their homes, their money, and their sanity.</p>
              </div>
              <div>
                <h4 className="text-gray-900 font-semibold mb-2">Gazeal Advance</h4>
                <p className="text-gray-600 text-sm">Agents get paid at sale agreed, not completion. No more waiting 6 months for your commission while the chain crawls along.</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 mb-8 shadow-xl">
            <p className="text-gray-700 italic text-lg mb-4">
              "We can't fix conveyancing overnight. But we can make sure that when a sale is agreed, it stays agreed. That's what Gazeal does. And that's not an April Fools joke."
            </p>
            <p className="text-gray-900 font-semibold">— Bryan Mansell, Gazeal</p>
          </div>

          <div className="text-white/40 text-sm text-center">
            <p className="mb-2">Gazeal Limited, 41 Devonshire Street, London W1G 7AJ</p>
            <p>
              <a href="https://gazeal.co.uk" className="hover:text-[#57D961] transition-colors">www.gazeal.co.uk</a>
              {' '} | {' '}
              <a href="https://advance.gazeal.co.uk" className="hover:text-[#57D961] transition-colors">advance.gazeal.co.uk</a>
            </p>
          </div>
        </div>
      </section>

      {/* CSS for animations */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes scroll-left {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        
        .animate-fade-in-up {
          opacity: 0;
          animation: fade-in-up 0.5s ease-out forwards;
        }
        
        .animate-slide-in {
          opacity: 0;
          animation: slide-in 0.6s ease-out forwards;
        }
        
        .animate-scroll-left {
          animation: scroll-left 25s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default SwiftAiPage;
